<script setup lang="ts">
import { computed } from "vue"
import type { MediaFormData } from "./media"
import FormSection from "@/forms/components/FormSection.vue"
import FormField from "@/forms/components/FormField.vue"
import { url } from "@/forms/validators"
import TagTaglist from "../tag/TagTaglist.vue"
import CompanyTaglist from "../companies/CompanyTaglist.vue"
import GameSeriesTaglist from "../gameseries/GameSeriesTaglist.vue"
import GameTaglist from "../games/GameTaglist.vue"
import GameConsoleTaglist from "../gameconsole/GameConsoleTaglist.vue"

const props = withDefaults(defineProps<{
  modelValue: MediaFormData
  disabled?: boolean
}>(), {
  disabled: false
})

const emit = defineEmits<{
  "update:modelValue": [value: MediaFormData]
}>()

function patch<K extends keyof MediaFormData>(key: K, value: MediaFormData[K]): void {
  emit("update:modelValue", { ...props.modelValue, [key]: value })
}

const extension = computed(() => {
  const i = props.modelValue.filename.lastIndexOf(".")
  return i >= 0 ? props.modelValue.filename.substring(i) : ""
})

const urlValidator = url()
</script>

<template>
  <div class="flex flex-col gap-6">
    <FormSection id="identity" title="Identität">
      <FormField name="basename" label="Name der Datei">
        <div class="flex items-center">
          <input
            data-testid="media-basename"
            :value="modelValue.basename"
            type="text"
            class="input input-bordered w-full"
            pattern="^[a-zA-z0-9_.,\- ()]+$"
            :disabled="disabled"
            @input="patch('basename', ($event.target as HTMLInputElement).value)"
          >
          <span class="self-center font-mono ml-2" v-text="extension" />
        </div>
      </FormField>
    </FormSection>

    <FormSection id="rights" title="Rechte & Quelle">
      <FormField name="copyright" label="Copyright-Hinweis">
        <input
          data-testid="media-copyright"
          :value="modelValue.copyright"
          type="text"
          class="input input-bordered w-full"
          :disabled="disabled"
          @input="patch('copyright', ($event.target as HTMLInputElement).value)"
        >
      </FormField>

      <FormField
        name="copyrightUrl"
        label="Copyright-URL"
        :validate="urlValidator"
      >
        <input
          data-testid="media-copyright-url"
          :value="modelValue.copyrightUrl"
          type="url"
          class="input input-bordered w-full"
          :disabled="disabled"
          @input="patch('copyrightUrl', ($event.target as HTMLInputElement).value)"
        >
      </FormField>

      <FormField name="license" label="Lizenz-Hinweis">
        <input
          data-testid="media-license"
          :value="modelValue.license"
          type="text"
          class="input input-bordered w-full"
          :disabled="disabled"
          @input="patch('license', ($event.target as HTMLInputElement).value)"
        >
      </FormField>

      <FormField
        name="licenseUrl"
        label="Lizenz-URL"
        :validate="urlValidator"
      >
        <input
          data-testid="media-license-url"
          :value="modelValue.licenseUrl"
          type="url"
          class="input input-bordered w-full"
          :disabled="disabled"
          @input="patch('licenseUrl', ($event.target as HTMLInputElement).value)"
        >
      </FormField>

      <FormField
        name="sourceUrl"
        label="Quelle-URL"
        :validate="urlValidator"
      >
        <input
          data-testid="media-source-url"
          :value="modelValue.sourceUrl"
          type="url"
          class="input input-bordered w-full"
          :disabled="disabled"
          @input="patch('sourceUrl', ($event.target as HTMLInputElement).value)"
        >
      </FormField>

      <FormField name="watermark" label="Wasserzeichen">
        <label class="flex items-center gap-2">
          <input
            data-testid="media-watermark"
            :checked="modelValue.watermark"
            type="checkbox"
            class="checkbox checkbox-primary"
            :disabled="disabled"
            @change="patch('watermark', ($event.target as HTMLInputElement).checked)"
          >
          <span class="label-text">Wasserzeichen anwenden</span>
        </label>
      </FormField>
    </FormSection>

    <FormSection id="assignments" title="Zuordnungen">
      <FormField name="tags" label="Tags">
        <TagTaglist
          data-testid="media-tags"
          :model-value="modelValue.tags"
          :disabled="disabled"
          @update:model-value="(v) => patch('tags', v)"
        />
      </FormField>

      <FormField name="companies" label="Unternehmen">
        <CompanyTaglist
          data-testid="media-companies"
          :model-value="modelValue.companies"
          :disabled="disabled"
          @update:model-value="(v) => patch('companies', v)"
        />
      </FormField>

      <FormField name="gameSeries" label="Spielserien">
        <GameSeriesTaglist
          data-testid="media-gameseries"
          :model-value="modelValue.gameSeries"
          :disabled="disabled"
          @update:model-value="(v) => patch('gameSeries', v)"
        />
      </FormField>

      <FormField name="games" label="Spiele">
        <GameTaglist
          data-testid="media-games"
          :model-value="modelValue.games"
          :disabled="disabled"
          @update:model-value="(v) => patch('games', v)"
        />
      </FormField>

      <FormField name="gameConsoles" label="Konsolen">
        <GameConsoleTaglist
          data-testid="media-gameconsoles"
          :model-value="modelValue.gameConsoles"
          :disabled="disabled"
          @update:model-value="(v) => patch('gameConsoles', v)"
        />
      </FormField>
    </FormSection>
  </div>
</template>
